import { Summary } from "utils/insight/summary";
import { Conditions, conditionsEquals, copyConditionsAndRemoveAll, getConditionById, getConditionIds } from "utils/insight/conditions";
import { getNumericComparator } from "utils/collect";
export class InsightDataFactoryProvider {
    typeFactoryMap;
    constructor(typeFactoryMap) {
        this.typeFactoryMap = typeFactoryMap;
    }
    getInsightDataFactory(type) {
        const factory = this.typeFactoryMap[type];
        return factory;
    }
    static create(metadata, summaryFactory, associationFactory, features, minValueCount) {
        return new InsightDataFactoryProvider({
            ["Correlation"]: new InsightDataFactoryCorrelation(associationFactory, new InsightDataFactoryDescriptive(summaryFactory, metadata, minValueCount)),
            ["ModelDriver"]: new InsightDataFactoryModelDriver(features),
            ["Descriptive"]: new InsightDataFactoryDescriptive(summaryFactory, metadata, minValueCount),
            ["Difference"]: new InsightDataFactoryDifference(new InsightDataFactoryDescriptive(summaryFactory, metadata, 0 /* disable minValueCount for whats changed */))
        });
    }
}
export function filterDescriptiveValues(metadata, data, minValueCount) {
    const currentOther = data.values.find((v) => {
        const c = getConditionById(v.conditions, data.id);
        return c !== undefined && metadata.getColumn(c.id).getValueLabels()[c.value] == "{Other}";
    });
    const filtered = data.values.filter((v) => v.summary.getTotalN() >= minValueCount);
    if (currentOther === undefined && filtered.length + 1 == data.values.length) {
        // if only one value is filtered out and there is no other bucket, do not filter
        return data.values;
    }
    return filtered;
}
export class InsightDataFactoryDescriptive {
    summaryFactory;
    metadata;
    minValueCount;
    globalSummary;
    constructor(summaryFactory, metadata, minValueCount) {
        this.summaryFactory = summaryFactory;
        this.metadata = metadata;
        this.minValueCount = minValueCount;
        const g = summaryFactory.getSummary(Conditions.emptySet());
        if (g !== undefined) {
            this.globalSummary = g;
        }
        else {
            this.globalSummary = new Summary(Conditions.emptySet(), 0, 0, 0, 0);
        }
    }
    getInsightData(insightConditions) {
        if (insightConditions.ids?.length !== 1) {
            return undefined;
        }
        const conditions = Conditions.from(insightConditions.conditions);
        const ids = Array.from(new Set(insightConditions.ids.concat(getConditionIds(conditions)))).sort();
        const summaries = this.summaryFactory.getSummarySet(ids).getSummaries(conditions);
        let values = [];
        for (const summary of summaries) {
            const baseConditions = copyConditionsAndRemoveAll(summary.conditions, conditions);
            const base = this.summaryFactory.getSummary(baseConditions) ?? summary;
            const other = base.subtract(summary);
            values.push({
                conditions: baseConditions,
                constraint: copyConditionsAndRemoveAll(summary.conditions, baseConditions),
                summary: summary,
                other: other,
                isDifferent: true
            });
        }
        const id = insightConditions.ids[0];
        if (values.some((v) => v.summary.getTotalN() < this.minValueCount)) {
            const currentOther = values.find((v) => {
                const c = getConditionById(v.conditions, id);
                return c !== undefined && this.metadata.getColumn(c.id).getValueLabels()[c.value] == "{Other}";
            });
            if (currentOther !== undefined) {
                const remainingValues = [];
                for (const value of values) {
                    if (conditionsEquals(value.conditions, currentOther.conditions)) {
                        continue;
                    }
                    else if (value.summary.getTotalN() < this.minValueCount) {
                        currentOther.summary = currentOther.summary.add(value.summary);
                        currentOther.other = currentOther.other.add(value.other);
                    }
                    else {
                        remainingValues.push(value);
                    }
                }
                values = remainingValues;
                values.push(currentOther);
            }
        }
        if (values.length == 0) {
            return undefined;
        }
        return {
            type: "Descriptive",
            id: id,
            conditions: conditions,
            values: values,
            globalSummary: this.globalSummary
        };
    }
}
export class InsightDataFactoryDifference {
    delegateFactory;
    constructor(delegateFactory) {
        this.delegateFactory = delegateFactory;
    }
    getInsightData(insightConditions) {
        const data = this.delegateFactory.getInsightData(insightConditions);
        if (data === undefined) {
            return undefined;
        }
        return {
            ...data,
            type: "Difference"
        };
    }
}
export class InsightDataFactoryCorrelation {
    associationFactory;
    delegateFactory;
    constructor(associationFactory, delegateFactory) {
        this.associationFactory = associationFactory;
        this.delegateFactory = delegateFactory;
    }
    getInsightData(insightConditions) {
        if (insightConditions.ids?.length !== 2) {
            return undefined;
        }
        const conditions = Conditions.from(insightConditions.conditions);
        const [id, conditionId] = insightConditions.ids;
        const descriptive = this.delegateFactory.getInsightData({
            ids: [conditionId],
            conditions: conditions
        });
        if (descriptive === undefined) {
            return undefined;
        }
        return {
            ...descriptive,
            type: "Correlation",
            r2: this.associationFactory.getAssociation(id, conditionId)
        };
    }
}
export class InsightDataFactoryModelDriver {
    features;
    scale = undefined;
    constructor(features) {
        this.features = features;
    }
    getScale() {
        if (this.scale !== undefined) {
            return this.scale;
        }
        const scale = Object.values(this.features)
            .flatMap((set) => set.getFeatures().map((feature) => Math.abs(feature.value)))
            .reduce((x, y) => Math.max(x, y), 0);
        this.scale = scale;
        return scale;
    }
    getInsightData(insightConditions) {
        if (insightConditions.ids?.length !== 1) {
            return undefined;
        }
        const [id] = insightConditions.ids;
        const conditions = Conditions.from(insightConditions.conditions || []);
        const models = Object.keys(this.features)
            .map((modelName) => ({
            modelName,
            features: this.features[modelName].subset([id]).getFeatures(conditions)
        }))
            .filter((model) => model.features.length > 0)
            .sort(getNumericComparator((model) => model.features.map((feature) => feature.value).reduce((x, y) => x + y, 0) / model.features.length));
        if (models.length == 0) {
            return undefined;
        }
        return {
            type: "ModelDriver",
            id: id,
            conditions: conditions,
            models: models,
            scale: this.getScale()
        };
    }
}
